
import {
  computed,
  defineComponent,
  nextTick,
  ref,
  toRef,
  watch,
  onMounted,
} from "vue";
import { Form, Field, ErrorMessage, FieldArray } from "vee-validate";
import { TranslationService } from "@/core/services/TranslationService";
import { FormatMethods, getMaxLengthErrorMessage } from "@/helpers/data-format";
import { SiteApi } from "@/services/api/Site.service";
import * as Yup from "yup";
import { ICloneUser } from "@/interfaces/iUser";
import { hideModal } from "@/core/helpers/dom";
import { useToast } from "vue-toastification";
import { UserAPI } from "@/services/api/User.service";
import Datepicker from "@vuepic/vue-datepicker";
import moment from "moment-timezone";
import VZPhone from "@/components/VZPhone.vue";
export default defineComponent({
  name: "form-user-modal",
  emits: ["clone-submit", "on-cancel"],
  props: {
    user: { type: Object, default: null },
  },
  components: {
    Form,
    Field,
    ErrorMessage,
    FieldArray,
    Datepicker,
    VZPhone,
  },
  setup(props, { emit }) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const toast = useToast();
    const cloneSuccessMsg = t("pages.user.userCloneSuccess");
    const newTargetCloneModalRef = ref<null | HTMLElement>(null);
    const cloneUserRef = ref<null | HTMLFormElement>(null);
    const loading = ref(false);
    const siteValidation = ref(false);
    const passwordTrue = ref<boolean>(false);
    const confirmPasswordTrue = ref<boolean>(false);
    let userObj = ref<any>(props.user);
    const cloneData = toRef(props, "user");
    let userClone: ICloneUser;
    let cloneTargetData = ref<any>({});
    let cloneUserData = ref({
      validationSchema: Yup.object({
        firstName: Yup.string()
          .max(30, getMaxLengthErrorMessage(t("common.firstName"), 30))
          .required(t("common.firstName") + " " + t("common.isRequired"))
          .matches(/^(?!\s*$)[A-Za-z\s]+$/, t("validation.notStartWithNum"))
          .nullable(),
        lastName: Yup.string()
          .max(30, getMaxLengthErrorMessage(t("common.lastName"), 30))
          .required(t("common.lastName") + " " + t("common.isRequired"))
          .matches(/^(?!\s*$)[A-Za-z\s]+$/, t("validation.notStartWithNum"))
          .nullable(),
        email: Yup.string()
          .email()
          .max(100, getMaxLengthErrorMessage(t("common.email"), 100))
          .matches(
            /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/,
            t("validation.emailValidation")
          )
          .required(t("common.email") + " " + t("common.isRequired")),
        password: Yup.string()
          .max(30, getMaxLengthErrorMessage(t("common.password"), 30))
          .required(t("common.password") + " " + t("common.isRequired"))
          .matches(
            /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%&*()]).{8,}/,
            t("validation.passwordValidation")
          ),
        password_confirmation: Yup.string()
          .max(
            30,
            getMaxLengthErrorMessage(t("pages.user.confirmPassword"), 30)
          )
          .oneOf([Yup.ref("password"), null], "Passwords must match")
          .required(
            t("pages.user.confirmPassword") + " " + t("common.isRequired")
          ),
        // phoneNumber: Yup.string().required().label(t("common.phoneNumber")),
        startDate: Yup.date()
          .nullable()
          .required(t("common.startDate") + " " + t("common.isRequired")),
        endDate: Yup.date()
          .nullable()
          .min(Yup.ref("startDate"), t("common.endDateValidation")),
        birthday: Yup.string()
          .required(t("validation.birthday"))
          .nullable()
          .test("valid-birthday", t("validation.birthday"), function (value) {
            if (!value) {
              return this.createError({
                path: this.path,
                message: "Birthday is required",
              });
            }
            const selectedDate = new Date(value);
            const minBirthdate = new Date();
            minBirthdate.setFullYear(minBirthdate.getFullYear() - 16);
            if (selectedDate > minBirthdate) {
              return false;
            }
            return true;
          }),
        defaultPhone: Yup.string().label(t("common.defaultPhone")),
        phoneModel: Yup.array().of(
          Yup.object().shape({
            phoneNumber: Yup.string().label(t("pages.user.phoneNumber")),
          })
        ),
      }),
    });
    const initialValues = ref({
      phoneModel: [{ id: null, type: "CALL", defaultPhone: true }],
    });
    userObj.value = computed(() => {
      return props.user;
    }).value;

    const discard = () => {
      var appElement = document.getElementById("app");
      if (appElement) {
        appElement.click();
      }
      validationErrors.value = [];
      const accordions = document.getElementsByClassName(
        "accordion-button fs-5 fw-bold text-black-50"
      );
      const accorBody = document.getElementsByClassName("accor-body");
      for (let i = 0; i < accordions.length; i++) {
        accordions[i].className =
          "accordion-button fs-5 fw-bold text-black-50 collapsed";
      }
      for (let i = 0; i < accorBody.length; i++) {
        accorBody[i].className = "accordion-collapse collapse accor-body";
      }
      clearFormData();
      hideModal(newTargetCloneModalRef.value);
      nextTick(() => {
        cloneUserRef?.value?.resetForm();
        clearFormData();
      });
      emit("on-cancel", null);
    };
    const minDate = computed(() => {
      return moment().format("YYYY-MM-DD");
    });
    const format = computed(() => {
      return FormatMethods;
    });
    const displayDate = computed(() => {
      return process.env.VUE_APP_DATE_PICKER_FORMAT || "yyyy-MM-dd";
    });
    const clearPassword = (e) => {
      passwordTrue.value = true;
    };
    const clearConfirmPassword = (e) => {
      confirmPasswordTrue.value = true;
    };
    const setUserCloneData = async () => {
      try {
        const [userSites, userForms] = await Promise.all([
          SiteApi.getInstance().getSiteUserList({
            size: 10000,
            userIds: userObj.value.webId,
          }),
          UserAPI.getInstance().getUserForms({
            userIds: userObj.value.webId,
            size: 100000,
          }),
        ]);
        if (userSites.content && userSites.content.length > 0) {
          cloneUserData.value["sites"] = userSites.content;
        }
        if (userForms.content && userForms.content.length > 0) {
          cloneUserData.value["forms"] = userForms.content;
        }
        cloneTargetData.value["forms"] = userForms.content.map((obj: any) => {
          return obj.siteId;
        });
        cloneTargetData.value["sites"] = userSites.content.map((obj: any) => {
          return obj.webId;
        });
      } catch (error) {
        console.log("error");
      }
    };
    const submitCloneData = async (values) => {
      /*** START CREATING OBJECT FOR FORM SITE ***/
      const cloneTargetForms = cloneTargetData?.value?.["forms"] || [];
      const userForms = cloneUserData?.value?.["forms"] || [];

      const formSiteIdsObj = cloneTargetForms.map((siteId: number) => {
        const matchingForm = userForms.find(
          (form: any) => form.siteId === siteId
        );

        return {
          formId: matchingForm?.formDefinitionId,
          siteId: siteId,
        };
      });
      /*** END CREATING OBJECT FOR FORM SITE ***/
      console.log("formSiteIdsObj", formSiteIdsObj);
      try {
        if (validationErrors.value.includes(true)) {
          return;
        }
        userClone = {} as ICloneUser;
        userClone.firstName = cloneTargetData.value.firstName;
        userClone.lastName = cloneTargetData.value.lastName;
        userClone.email = cloneTargetData.value.email;
        userClone.password = cloneTargetData.value.password;
        userClone.password_confirmation =
          cloneTargetData.value.password_confirmation;
        userClone.startDate = cloneTargetData.value.startDate;

        const dateFormat = "YYYY-MM-DDTHH:mm:ssZ";
        if (cloneTargetData.value.endDate) {
          const endMoment = moment(cloneTargetData.value.endDate).endOf("day");
          const formattedEndDate = endMoment.format(dateFormat);
          userClone.endDate = formattedEndDate;
        }

        userClone.birthday = cloneTargetData.value.birthday;
        userClone.phoneNumber = "";
        userClone.userPermissionGlobalModel = {
          permissionIds: [],
        };
        userClone.userRoleGlobalModel = {
          roleIds: [],
        };
        userClone.userFormGlobalModel = {
          formSiteIds: formSiteIdsObj?.length > 0 ? formSiteIdsObj : [],
        };
        userClone.siteUserGlobalModel = {
          userSiteIds:
            cloneTargetData?.value?.["sites"]?.length > 0
              ? cloneTargetData?.value?.["sites"]
              : [],
        };
        if (!siteValidation.value) {
          loading.value = true;
          const startMoment = moment(cloneTargetData.value.startDate).startOf(
            "day"
          );
          const formattedStartDate = startMoment.format(dateFormat);
          userClone.startDate = formattedStartDate;
          const user = await UserAPI.getInstance().submitCloneUser(userClone);
          const phoneModel = values.phoneModel.map((phone, index) => {
            return {
              phoneNumber: phone?.phoneNumber?.replace(/[^\d+]/g, ""),
              type: phone.type,
              webId: phone.webId,
              defaultPhone: index === values.defaultPhone,
            };
          });
          if (user?.webId) {
            await UserAPI.getInstance().updatePhoneModel(
              user.webId as number,
              phoneModel
            );
          }
          toast.success(cloneSuccessMsg);
          loading.value = false;
          discard();
          emit("clone-submit");
        }
      } catch (error) {
        loading.value = false;
        console.log(error);
      }
    };

    // console.log("My Clone User Data:", cloneUserData);

    const clearFormData = async () => {
      await nextTick(() => {
        cloneUserRef?.value?.resetForm();
        cloneTargetData.value = {};
      });
    };

    const clearEmailField = (e) => {
      setTimeout(() => {
        e.target.value = "";
        cloneTargetData.value.password = "";
      }, 100);
    };
    const selectAll = (element, array) => {
      const isChecked = element.target.checked;

      if (isChecked) {
        cloneTargetData.value[array] = cloneUserData.value[array]?.map(
          (obj) => {
            switch (array) {
              case "cloneUsers":
                return obj.userId;
              case "cloneSections":
                return obj.formSectionId;
              case "forms":
                return obj.siteId;
              default:
                return obj.webId;
            }
          }
        );
      } else {
        cloneTargetData.value[array] = [];
      }
    };

    // const selectAll = (element, array) => {
    //   if (element.srcElement.checked) {
    //     cloneTargetData.value[array] = cloneUserData.value[array]?.map(
    //       (obj) => {
    //         if (array == "cloneUsers") {
    //           return obj.userId;
    //         } else if (array == "cloneSections") {
    //           return obj.formSectionId;
    //         } else if (array == "forms") {
    //           return obj.formDefinitionId;
    //         }
    //         return obj.webId;
    //       }
    //     );
    //   } else {
    //     cloneTargetData.value[array] = [];
    //   }
    // };
    const getClass = (role) => {
      let className = "bg-success";
      if (role == "Manager") {
        className = "bg-info";
      } else if (role == "User") {
        className = "bg-primary";
      } else if (role == "Admin") {
        className = "bg-success";
      }
      return className;
    };

    const isChecked = (array, id) => {
      if (cloneTargetData?.value?.["sites"]?.length <= 0) {
        siteValidation.value = true;
      } else if (cloneTargetData?.value?.["sites"]?.length > 0) {
        siteValidation.value = false;
      }
      return (
        cloneTargetData?.value[array]?.filter((objId) => objId == id).length > 0
      );
    };

    const updateCloneArray = (element, id, objectId) => {
      if (element.srcElement.checked) {
        if (
          cloneTargetData?.value[objectId] == undefined ||
          cloneTargetData?.value[objectId].length == 0
        ) {
          cloneTargetData.value[objectId] = [];
        }
        cloneTargetData?.value[objectId].push(id);
      } else {
        cloneTargetData.value[objectId] = cloneTargetData?.value[
          objectId
        ].filter((objId) => objId != id);
      }
    };
    const minEndDate = computed(() => {
      const today = moment().format("YYYY-MM-DD");
      const startDate = moment(cloneTargetData.value.startDate).format(
        "YYYY-MM-DD"
      );
      if (today >= startDate) {
        return today;
      } else {
        return startDate;
      }
    });
    watch(
      cloneData,
      (newValue) => {
        userObj.value = newValue;
        clearFormData();
        setUserCloneData();
      },
      { immediate: true }
    );
    const validationErrors = ref([]) as any;
    const handleValidation = (value, index) => {
      validationErrors.value[index] = value;
    };
    onMounted(() => {
      setTimeout(() => {
        clearFormData();
      }, 250);
    });
    return {
      t,
      cloneUserData,
      submitCloneData,
      cloneTargetData,
      getClass,
      updateCloneArray,
      newTargetCloneModalRef,
      format,
      cloneUserRef,
      discard,
      userObj,
      isChecked,
      selectAll,
      loading,
      initialValues,
      displayDate,
      minDate,
      siteValidation,
      defaultPhoneIndex: 1,
      validationErrors,
      handleValidation,
      minEndDate,
      clearPassword,
      passwordTrue,
      clearConfirmPassword,
      confirmPasswordTrue,
    };
  },
});
